import React, { useState, useEffect } from 'react';
import { submitFormData } from '../../../../pages/api';

const VisualInspection = ({ formData, onFormDataChange }) => {
    const {
        backRatingLabelData = [],
        barcodesRFIDData = [],
        moduleInspectionData = Array.from({ length: 10 }, () => ({ moduleNumber: '', status: 'Ok', remarks: '' })),
        nonConformanceObservations = '',
    } = formData;

    useEffect(() => {
        // Check if formData already has prefilled data
        const hasFormData =
            backRatingLabelData.length > 0 ||
            barcodesRFIDData.length > 0 ||
            moduleInspectionData.some((module) => module.moduleNumber.trim() !== '') ||
            nonConformanceObservations.trim() !== '';

        if (!hasFormData) {
            // Step 1: Fetch metadata from sessionStorage
            const metadata = JSON.parse(sessionStorage.getItem('metadata'));

            let prefilledBackRatingLabelData = [];
            let prefilledBarcodesRFIDData = [];
            let prefilledModuleInspectionData = Array.from({ length: 10 }, () => ({ moduleNumber: '', status: 'Ok' }));
            let prefilledNonConformance = '';

            // Step 2: Check metadata for prefilled data
            if (
                metadata &&
                metadata.post &&
                metadata.post.VisualInspections_post
            ) {
                const { BackRatingLabel, BarcodesRFID } = metadata.post.VisualInspections_post;

                prefilledBackRatingLabelData = BackRatingLabel || [];
                prefilledBarcodesRFIDData = BarcodesRFID || [];
            }

            // Step 3: Fetch inspectionData from sessionStorage
            const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData'));

            // Step 4: Check inspectionData for observations and non-conformance data
            if (
                inspectionData &&
                inspectionData.PostLamination &&
                inspectionData.PostLamination.VisualInspections_post
            ) {
                const {
                    BackRatingLabel,
                    BarcodesRFID,
                    ModuleInspection,
                    nonConformance: inspectionNonConformance,
                } = inspectionData.PostLamination.VisualInspections_post;

                prefilledBackRatingLabelData = BackRatingLabel || [];
                prefilledBarcodesRFIDData = BarcodesRFID || [];
                prefilledModuleInspectionData = ModuleInspection || Array.from({ length: 10 }, () => ({ moduleNumber: '', status: 'Ok' }));
                prefilledNonConformance = inspectionNonConformance || '';
            }

            // Step 5: Update formData only if prefilled data exists
            if (
                prefilledBackRatingLabelData.length > 0 ||
                prefilledBarcodesRFIDData.length > 0 ||
                prefilledModuleInspectionData.some((module) => module.moduleNumber.trim() !== '') ||
                prefilledNonConformance.trim() !== ''
            ) {
                onFormDataChange({
                    ...formData,
                    backRatingLabelData: prefilledBackRatingLabelData,
                    barcodesRFIDData: prefilledBarcodesRFIDData,
                    moduleInspectionData: prefilledModuleInspectionData,
                    nonConformanceObservations: prefilledNonConformance,
                });
            }
        }
    }, [formData, onFormDataChange]);

    // Handle observation input change for the tables
    const handleObservationChange = (table, index, field, value) => {
        const updateData = (data, setter) => {
            const updated = [...data];
            updated[index][field] = value;
            setter(updated);
        };

        if (table === 'backRatingLabel') {
            const updatedData = [...backRatingLabelData];
            updatedData[index][field] = value;
            onFormDataChange({ ...formData, backRatingLabelData: updatedData });
        } else if (table === 'barcodesRFID') {
            const updatedData = [...barcodesRFIDData];
            updatedData[index][field] = value;
            onFormDataChange({ ...formData, barcodesRFIDData: updatedData });
        }
    };

    // Handle module inspection input change
    const handleModuleInspectionChange = (index, field, value) => {
        const updatedData = [...moduleInspectionData];
        updatedData[index][field] = value;
        // Clear remarks if status changes to "Ok"
        if (field === 'status' && value === 'Ok') {
            updatedData[index].remarks = '';
        }
        onFormDataChange({ ...formData, moduleInspectionData: updatedData });
    };

    // Handle non-conformance input change
    const handleNonConformanceChange = (value) => {
        onFormDataChange({ ...formData, nonConformanceObservations: value });
    };

    // Function to send observations to the API
    const submitObservations = async () => {
        const projectId = localStorage.getItem('selectedProject');
        const inspectionDate = sessionStorage.getItem('inspectionDate');
        const inspectionShift = sessionStorage.getItem('inspectionShift');
        const inspectorName = sessionStorage.getItem('username');
        const inspectionSupplier = sessionStorage.getItem('inspectionSupplier');

        const combinedBackRatingLabelData = backRatingLabelData.map((item) => ({
            product: item.product,
            specifications: item.specifications,
            frequency: item.frequency,
            observation1: item.observation1 || '',
            observation2: item.observation2 || '',
        }));

        const combinedBarcodesRFIDData = barcodesRFIDData.map((item) => ({
            product: item.product,
            specifications: item.specifications,
            frequency: item.frequency,
            observation1: item.observation1 || '',
            observation2: item.observation2 || '',
        }));

        const payload = {
            projectId,
            inspectionSupplier,
            inspectionDate,
            inspectionShift,
            inspectorName,
            status: 'completed',
            PostLamination: {
                VisualInspections_post: {
                    BackRatingLabel: combinedBackRatingLabelData,
                    BarcodesRFID: combinedBarcodesRFIDData,
                    ModuleInspection: moduleInspectionData.map((item) => ({
                        moduleNumber: item.moduleNumber,
                        status: item.status,
                        remarks: item.remarks || '', // Include remarks in payload
                    })),                    
                    nonConformance: nonConformanceObservations || '',
                },
            },
        };

        try {
            await submitFormData(payload);
            alert('Observations submitted successfully');
        } catch (error) {
            console.error('Error submitting observations:', error);
            alert('Failed to submit observations');
        }
    };

    return (
        <div className="mx-auto bg-gray-50 min-h-screen">
            {/* Back Rating Label Table */}
            <div className="mt-8">
                <h4 className="text-xl font-bold mb-6 text-gray-800">Back Rating Label</h4>
                <div className="overflow-x-auto shadow-lg rounded-lg">
                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                        <thead>
                            <tr className="bg-gradient-to-r from-[#424752] to-[#5a6271] text-white text-center">
                                <th className="py-4 px-6 min-w-[150px]">Product</th>
                                <th className="py-4 px-6 min-w-[200px]">Specifications</th>
                                <th className="py-4 px-6 min-w-[150px]">Frequency</th>
                                <th className="py-4 px-6 min-w-[200px]">Observation 1</th>
                                <th className="py-4 px-6 min-w-[200px]">Observation 2</th>
                            </tr>
                        </thead>
                        <tbody>
                            {backRatingLabelData.map((item, index) => (
                                <tr
                                    key={index}
                                    className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                                >
                                    <td className="py-4 px-6 text-gray-700">{item.product}</td>
                                    <td className="py-4 px-6 text-gray-700">{item.specifications}</td>
                                    <td className="py-4 px-6 text-gray-700">{item.frequency}</td>
                                    <td className="py-4 px-6">
                                        <input
                                            type="text"
                                            value={item.observation1 || ''}
                                            onChange={(e) => handleObservationChange('backRatingLabel', index, 'observation1', e.target.value)}
                                            placeholder="Enter Observation 1"
                                            className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                        />
                                    </td>
                                    <td className="py-4 px-6">
                                        <input
                                            type="text"
                                            value={item.observation2 || ''}
                                            onChange={(e) => handleObservationChange('backRatingLabel', index, 'observation2', e.target.value)}
                                            placeholder="Enter Observation 2"
                                            className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Module Inspection Section */}
            <div className="mt-8 bg-white p-6 rounded-lg shadow-lg">
                <h4 className="text-xl font-bold mb-4 text-gray-800">Module Final Visual Inspection</h4>
                <p className="text-sm text-gray-600 mb-6">
                    (Cell defects/Frame defects/Sealant defects/JB defects/Glass defects and etc. 5 modules/every 4hrs)
                </p>
                <div className="grid grid-cols-2 gap-6">
                    {moduleInspectionData.map((module, index) => (
                        <div key={index} className="flex flex-col gap-4">
                            <div className="flex items-center gap-4">
                                <input
                                    type="text"
                                    value={module.moduleNumber}
                                    onChange={(e) => handleModuleInspectionChange(index, 'moduleNumber', e.target.value)}
                                    placeholder={`Module ${index + 1} Number`}
                                    className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                />
                                <select
                                    value={module.status}
                                    onChange={(e) => handleModuleInspectionChange(index, 'status', e.target.value)}
                                    className="border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                >
                                    <option value="Ok">Ok</option>
                                    <option value="Not Ok">Not Ok</option>
                                </select>
                            </div>
                            {module.status === 'Not Ok' && (
                                <input
                                    type="text"
                                    value={module.remarks || ''}
                                    onChange={(e) => handleModuleInspectionChange(index, 'remarks', e.target.value)}
                                    placeholder="Enter remarks for Not Ok status"
                                    className="w-full border border-red-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-400 transition-all duration-150"
                                />
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Barcodes/RFID Table */}
            <div className="mt-8">
                <h4 className="text-xl font-bold mb-6 text-gray-800">Barcodes/RFID</h4>
                <div className="overflow-x-auto shadow-lg rounded-lg">
                    <table className="min-w-full bg-white rounded-lg overflow-hidden">
                        <thead>
                            <tr className="bg-gradient-to-r from-[#424752] to-[#5a6271] text-white text-center">
                                <th className="py-4 px-6 min-w-[150px]">Product</th>
                                <th className="py-4 px-6 min-w-[200px]">Specifications</th>
                                <th className="py-4 px-6 min-w-[150px]">Frequency</th>
                                <th className="py-4 px-6 min-w-[200px]">Observation 1</th>
                                <th className="py-4 px-6 min-w-[200px]">Observation 2</th>
                            </tr>
                        </thead>
                        <tbody>
                            {barcodesRFIDData.map((item, index) => (
                                <tr
                                    key={index}
                                    className={`border-b last:border-none ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-gray-100 transition-colors duration-200`}
                                >
                                    <td className="py-4 px-6 text-gray-700">{item.product}</td>
                                    <td className="py-4 px-6 text-gray-700">{item.specifications}</td>
                                    <td className="py-4 px-6 text-gray-700">{item.frequency}</td>
                                    <td className="py-4 px-6">
                                        <input
                                            type="text"
                                            value={item.observation1 || ''}
                                            onChange={(e) => handleObservationChange('barcodesRFID', index, 'observation1', e.target.value)}
                                            placeholder="Enter Observation 1"
                                            className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                        />
                                    </td>
                                    <td className="py-4 px-6">
                                        <input
                                            type="text"
                                            value={item.observation2 || ''}
                                            onChange={(e) => handleObservationChange('barcodesRFID', index, 'observation2', e.target.value)}
                                            placeholder="Enter Observation 2"
                                            className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Non-Conformance Observations Section */}
            <div className="mt-8 bg-white p-6 rounded-lg shadow-lg">
                <h4 className="text-xl font-bold mb-4 text-gray-800">Non-Conformance Observations</h4>
                <textarea
                    value={nonConformanceObservations}
                    onChange={(e) => handleNonConformanceChange(e.target.value)}
                    placeholder="Enter non-conformance observations"
                    rows="4"
                    className="w-full border border-gray-300 p-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-150"
                />
            </div>

            {/* Submit Button */}
            <div className="mt-8">
                <button
                    onClick={submitObservations}
                    className="bg-gradient-to-r from-[#424752] to-[#5a6271] text-white px-8 py-3 rounded-lg hover:from-[#5a6271] hover:to-[#424752] focus:outline-none focus:ring-2 focus:ring-blue-400 transition-all duration-300"
                >
                    Submit
                </button>
            </div>
        </div>
    );
};

export default VisualInspection;